import React from 'react';
import { DAILY_GOAL_MINUTES } from '../../types';

interface DailyGoalProgressProps {
  progress: number; // 0-100
  minutes: number;
  goalReached: boolean;
  size?: 'small' | 'medium' | 'large';
}

const DailyGoalProgress: React.FC<DailyGoalProgressProps> = ({
  progress,
  minutes,
  goalReached,
  size = 'medium',
}) => {
  // Size configurations
  const sizes = {
    small: {
      container: 'w-16 h-16',
      strokeWidth: 4,
      radius: 28,
      fontSize: 'text-xs',
      iconSize: 'text-lg',
    },
    medium: {
      container: 'w-24 h-24',
      strokeWidth: 6,
      radius: 42,
      fontSize: 'text-sm',
      iconSize: 'text-2xl',
    },
    large: {
      container: 'w-32 h-32',
      strokeWidth: 8,
      radius: 56,
      fontSize: 'text-base',
      iconSize: 'text-3xl',
    },
  };

  const config = sizes[size];
  const circumference = 2 * Math.PI * config.radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Colors based on progress
  const getProgressColor = () => {
    if (goalReached) return '#10B981'; // emerald-500
    if (progress >= 66) return '#F59E0B'; // amber-500
    if (progress >= 33) return '#6366F1'; // indigo-500
    return '#6366F1'; // indigo-500
  };

  return (
    <div className={`relative ${config.container}`}>
      {/* Background circle */}
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="50%"
          cy="50%"
          r={config.radius}
          className="stroke-gray-200 dark:stroke-slate-600"
          strokeWidth={config.strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="50%"
          cy="50%"
          r={config.radius}
          stroke={getProgressColor()}
          strokeWidth={config.strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {goalReached ? (
          <>
            <span className={config.iconSize}>{'\u{1F3C6}'}</span>
            <span className={`${config.fontSize} font-bold text-emerald-600 dark:text-emerald-400`}>Done!</span>
          </>
        ) : (
          <>
            <span className={`${config.fontSize} font-bold text-gray-800 dark:text-white`}>
              {minutes}/{DAILY_GOAL_MINUTES}
            </span>
            <span className={`${config.fontSize} text-gray-500 dark:text-gray-400`}>min</span>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyGoalProgress;
